import {
  CompilationStatus,
  type ShareGroupVideoAudience,
  Compilation,
} from "@memento/types/compilation";

import { Fetcher } from "src/libs/fetcher";
import { getCompilationSendEndpointUrl } from "src/libs/api-endpoint-lib";
import constants from "src/constants";
import { toAbsolutePath } from "./urlLib";
import { GetAccessTokenFn } from "src/contexts/AuthProvider";
import { sendAnalyticsEvent } from "./analytics-lib";
import { TierLabel } from "@memento/types/subscription";

export const sendCompilation = async ({
  getAccessToken,
  compilationId,
  groupVideoAudience,
  emailSubject,
  emailBody,
  recipients,
}: {
  getAccessToken: GetAccessTokenFn;
  compilationId: string;
  groupVideoAudience: ShareGroupVideoAudience;
  emailSubject: string;
  emailBody: string;
  recipients?: string;
}) => {
  const fetcher = Fetcher(getAccessToken);

  return fetcher(getCompilationSendEndpointUrl(compilationId), {
    method: "POST",
    body: {
      groupVideoAudience,
      emailSubject,
      emailBody,
      ...(recipients
        ? {
            recipient: {
              addresses: recipients,
            },
          }
        : {}),
    },
  });
};

export const getWatchCompilationUrl = ({
  compilationId,
  forRecipient = false,
  absolutePath = true,
  reactionsEnabled = false,
}: {
  compilationId: string;
  forRecipient?: boolean;
  absolutePath?: boolean;
  reactionsEnabled?: boolean;
}) => {
  const path = `/${compilationId}/${constants.slugs.compilationLink}${
    forRecipient && reactionsEnabled
      ? `?${constants.reactionRecipientViewUrlParam}=1`
      : ""
  }`;

  return absolutePath ? toAbsolutePath(path) : path;
};

export const sendRenderStartedAnalyticsEvent = ({
  compilationVersion,
  compilationTheme,
  cardCompilations = [],
  username,
  tierLabel,
  cardId,
  numberOfVideos,
  numberOfPhotos,
  numberOfContributors,
  numberOfInvites,
}: {
  compilationTheme?: string;
  compilationVersion?: number;
  cardCompilations?: Compilation[];
  username: string | undefined;
  tierLabel: TierLabel | undefined;
  cardId: string | undefined;
  numberOfVideos: number;
  numberOfPhotos: number;
  numberOfContributors: number;
  numberOfInvites: number;
}) => {
  const previousCompilations = cardCompilations.filter(
    (c) => c.compilationStatus === CompilationStatus.COMPLETE
  );
  const renderCountV2 = previousCompilations.filter(
    (c) => c.version === 2
  ).length;
  const renderCountV3 = previousCompilations.filter(
    (c) => c.version === 3
  ).length;

  sendAnalyticsEvent({
    event: "RenderStarted",
    userId: username,
    tierLabel,
    data: {
      cardId,
      rendererVersion: compilationVersion,
      renderTheme: compilationTheme,
      renderCountV2,
      renderCountV3,
      numberOfVideos,
      numberOfPhotos,
      numberOfContributors,
      numberOfInvites,
    },
  });
};

export const getRenderStartedAt = (compilation: Compilation) => {
  return compilation.version === 3
    ? compilation.renderRequest.status.startedAt || compilation.createdAt
    : compilation.createdAt;
};
